import Graphic from '@arcgis/core/Graphic';
import { differenceInMinutes } from 'date-fns';

import { FsPresenceDevice } from '@lib/api/entities';
import {
  AxonUsherAlertModel,
  HistoricalInterface,
  IDeviceMarker,
  STATE_BASED_NOTIFICATION_CATEGORIES,
} from '@lib/axon';
import { AxonNotificationIconService } from '@lib/axon/services/axon-notification-icon.service';
import { DEVICE_GLYPHS, OUTPOST_LIGHTPOST_SVG_TEMPLATE } from '@lib/axon/constants/device-icon-templates';
import { PresenceDeviceModels } from '@lib/axon/models/presence-device-model.enum';
import { SnCurrentOrganization } from '@lib/auth';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { CRITICAL_EVENT_TYPES, colorIsDark } from '@lib/common/helpers';
import { Maps, SnLonLat } from '@lib/geo/helpers';
import {
  FsMap,
  FsMapMarker,
  FsMarkerSymbolType,
  IMapMarkerOptions,
  IMapMarkerStyleOptions,
} from '@lib/geo/map';
import { COLORS, THEME } from '@lib/ui/theme';

import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

interface IBannerContent {
  data: AxonUsherAlertModel;
  icon: string;
}

export class FsPresenceMarker extends FsMapMarker implements IDeviceMarker {
  public icons: any[];
  public selected: boolean;
  public device: FsPresenceDevice;
  public updated: number = Date.now();
  public content = document.createElement('div');
  public destination: google.maps.LatLng;
  public banner: HTMLDivElement;
  public bannerContent: IBannerContent[] = [];
  public deviceHistory: HistoricalInterface[] = [];
  public hasHighPriorityHistory = false;
  public circle: google.maps.Circle;
  private _distanceToDestination: number;
  private _deltaLat: number;
  private _deltaLng: number;
  private _expectedMoves: number;
  private _actualMoves: number;
  private _followMode: boolean;
  private _currentOrganization?: SnCurrentOrganization;
  private _svgIconInitialized: boolean = false;

  protected static getIcon(device: FsPresenceDevice): string {
    switch (device.properties.entityType) {
      case 'bwc':
        return 'ab3';
      case 'vehicle':
        return 'car';
      case 'drone':
        return 'drone';
      case 'cluster':
        return 'cluster';
      case 'unknown':
        return 'ab3';
      default:
        return 'ab3';
    }
  }

  protected static getStyle(device: FsPresenceDevice): {
    fillColor: string;
  } {
    switch (device.properties.cameras?.status.toLowerCase()) {
      case 'recording':
        return {
          fillColor: COLORS['green-500'],
        };
      case 'idle':
        return {
          fillColor: COLORS['green-1000'],
        };
      default:
        return {
          fillColor: COLORS['grey-500'],
        };
    }
  }

  public static generateIcon(
    notification: string,
    axonNotificationIconService: AxonNotificationIconService,
  ): string {
    return axonNotificationIconService.getIconClass(notification);
  }

  public static onBannerHover(banner: HTMLDivElement, bannerContent: IBannerContent[]): void {
    const bannerConentElement = document.createElement('div');
    bannerConentElement.classList.add('fs__advanced-marker-icon-banner-expanded');
    bannerConentElement.innerHTML = bannerContent
      .map(
        (content) =>
          `
        <div class="fs_axon_marker_details">
          <div class="fs_axon_marker_icon">
            ${content.icon}
          </div>
          <div class="fs_axon_marker_text">
            <div class="fs_axon_marker_name_time">
              <span class="fs_axon_marker_name">${content.data.ownerName} - </span>
              <span class="fs_axon_marker_time">${new FsFormatDatePipe().transform(
                content.data.notification.notificationReceivedTime,
                'HH:mm',
              )}hrs</span>
            </div>
            <div class="fs_axon_marker_event">
              ${content.data.messageTranslation}
            </div>
          </div>
        </div>
        `,
      )
      .join(' ');

    banner.appendChild(bannerConentElement);

    bannerConentElement.onmouseleave = () => {
      bannerConentElement.remove();
    };
  }

  constructor(
    {
      map,
      device,
      cluster = null,
      selected,
      initialStyle,
      markerOptions,
    }: {
      map: FsMap;
      device: FsPresenceDevice;
      cluster?: any;
      initialStyle?: IMapMarkerStyleOptions;
      selected?: boolean;
      markerOptions?: IMapMarkerOptions;
    },
    private _axonNotificationIconService: AxonNotificationIconService,
    currentOrganization?: SnCurrentOrganization,
  ) {
    const icon = FsPresenceMarker.getIcon(device);
    const style = FsPresenceMarker.getStyle(device);
    const deviceId = device.properties.id;
    const deviceName =
      device.properties?.users?.[0]?.firstName && device.properties?.users?.[0]?.lastName
        ? `${device.properties.users[0].firstName} ${device.properties.users[0].lastName}`
        : device.properties?.name;

    super(
      map,
      {
        id: deviceId,
        type: FsMarkerSymbolType.CIRCLE,
        lonLat: {
          lat: device.geometry?.coordinates?.[1] || 0,
          lng: device.geometry?.coordinates?.[0] || 0,
        },
        icon: {
          fontSet: 'fs',
          fontIcon: icon,
        },
        style: {
          fillColor: style.fillColor,
          fillOpacity: 1,
          strokeOpacity: 1,
          scale: 1,
          border: '1px solid white',
          ...initialStyle,
        },
        iconLabel: {
          text: deviceName,
          top: 32,
          left: -5,
          fontSize: '14px',
        },
        title: deviceName,
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
        isAxonLayer: true,
        clickable: true,
        attributes: device,
        ...markerOptions,
      },
      cluster,
    );
    this._currentOrganization = currentOrganization;
    this.selected = selected;
    this.setIconLabel();
    this.setDevice(device);
    if (this.shouldUseSvgIcon()) {
      this.setSvgIcon();
    } else {
      this.setIcon();
      if (this.styles) {
        this.setStyles(this.content);
      }
    }
    this.content.classList.add('fs__center-marker');
    this.content.classList.add('fs__advanced-marker');
    this.content.classList.add('fs__advanced-feature-marker');
    this.getMarker().content = this.content;
    if (this._options.isAxonLayer && icon) {
      const status = this.device?.properties?.cameras?.status?.toLowerCase();
      if (status === 'recording') {
        this.content.classList.add('fs__axon-recording-status');
      } else {
        this.content.classList.remove('fs__axon-recording-status');
      }
    }
  }

  getIsStreaming(): boolean {
    return this.device.properties?.cameras?.cams?.some((cam) => cam.streamable);
  }

  getIsCameraActive(): boolean {
    return this.device.properties?.active;
  }

  getDeviceStatus(): string {
    return this.device.properties.cameras?.status;
  }

  isRecording(): boolean {
    return this.getDeviceStatus() === 'recording';
  }

  isBuffering(): boolean {
    return this.getDeviceStatus() === 'idle';
  }

  isInactive(): boolean {
    const status = this.getDeviceStatus();
    const inactiveStatuses = ['unknown', undefined, null];
    return inactiveStatuses.includes(status);
  }

  canStream(): boolean {
    return this.isRecording() || this.getIsStreaming();
  }

  setFollowMode(followMode: boolean): void {
    this._followMode = followMode;
  }

  getFollowMode(): boolean {
    return this._followMode;
  }

  getMarker(): google.maps.marker.AdvancedMarkerElement {
    const marker = super.getMarker();

    if (!(marker instanceof google.maps.marker.AdvancedMarkerElement)) {
      return null;
    }
    return marker;
  }

  /**
   * Used to set the destination.
   * @param latLng is the latitude and longitude of the marker/ device.
   */
  public setDestination(latLng: google.maps.LatLng): void {
    if (this.destination) {
      if (this.defaultMapSource === Maps.GOOGLE) {
        (this.getMarker() as unknown as AdvancedMarkerElement).position = {
          lat: this.destination.lat(),
          lng: this.destination.lng(),
        };
      } else {
        (this.getMarker() as unknown as Graphic[]).forEach(
          (graphic) => (graphic.geometry = this.destination as unknown as __esri.Point),
        );
      }
    }
    this.destination = latLng;
  }

  /**
   *
   * @param seconds is the
   */
  public setDeltas(seconds: number): void {
    const destination = this.destination;
    const current = this.getLonLat();
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (destination.lat() - current.lat) / this._expectedMoves;
    this._deltaLng = (destination.lng() - current.lon) / this._expectedMoves;
  }

  /**
   * Used to set the device for the marker.
   * @param device is the {@link FsPresenceDevice} to set.
   */
  public setDevice(device: FsPresenceDevice): void {
    this.device = device;
  }

  public checkNotifications(content: IBannerContent[]): void {
    const minuteDifference = 15;
    this.bannerContent = content.filter((c) => {
      const isStateBasedNotification = STATE_BASED_NOTIFICATION_CATEGORIES.includes(
        c.data.notification.category as any,
      );
      return (
        this.withinXMinutes(c.data.notification.notificationReceivedTime, minuteDifference) ||
        isStateBasedNotification
      );
    });

    // Sort notifications descending by timestamp
    this.bannerContent?.sort(
      (a, b) =>
        b.data.notification.notificationReceivedTime - a.data.notification.notificationReceivedTime,
    );

    if (this.bannerContent.length === 0) {
      this.removeNotification();
    } else if (this.banner) {
      const uniqueIcons = [...new Set(this.bannerContent.map((content) => content.icon))];
      this.banner.innerHTML = uniqueIcons.join(' ');

      // Update critical state after filtering notifications
      this._updateBannerCriticalState();

      const gmMarker:
        | (google.maps.marker.AdvancedMarkerElement & { axonNotifications?: AxonUsherAlertModel[] })
        | null = this.getMarker();
      gmMarker.axonNotifications = this.bannerContent.map((content) => content.data);
    }
  }

  private _updateBannerCriticalState(): void {
    if (!this.banner) {
      return;
    }

    const hasCriticalEvent = this.bannerContent.some((content) =>
      CRITICAL_EVENT_TYPES.includes(content.data?.notification?.category),
    );

    if (hasCriticalEvent) {
      this.banner.classList.add('fs__critical');
    } else {
      this.banner.classList.remove('fs__critical');
    }
  }

  public withinXMinutes(receivedTime: number, minutesDifference: number): boolean {
    return minutesDifference >= differenceInMinutes(new Date(), receivedTime);
  }

  /**
   * Used to update the styles of the marker.
   * @param styles is the {@link IMapMarkerStyleOptions} to set.
   */
  public updateStyles(styles?: IMapMarkerStyleOptions): void {
    this.styles = {
      fillColor: this.getStatusColor(),
      fillOpacity: 1,
      strokeOpacity: 1,
      scale: 1.2,
    };
    if (this.shouldUseSvgIcon()) {
      this.updateSvgIconColor();
    } else {
      this.setStyles(this.content);
    }
  }

  public getStatusColor(): string {
    const isOutpostSvg = this.shouldUseSvgIcon();
    const status = this.device.properties.cameras?.status.toLowerCase();
    
    if (isOutpostSvg) {
      switch (status) {
        case 'recording':
        case 'idle':
          return THEME['axon-device-online'];
        default:
          return THEME['axon-device-offline'];
      }
    }
    
    switch (status) {
      case 'recording':
        return COLORS['green-500'];
      case 'idle':
        return COLORS['green-1000'];
      default:
        return COLORS['grey-500'];
    }
  }

  /**
   * Used to set the styles of the marker.
   * @param content is the {@link HTMLDivElement} to set.
   */
  public setStyles(content: HTMLDivElement): void {
    const borderColor = this.styles.strokeColor || '#fff';
    const borderWeight = this.styles.strokeWeight || 1;

    if (this.styles.fillOpacity > 0) {
      const colorBasedOnFillColor = colorIsDark(this.styles.fillColor)
        ? COLORS['grey-0']
        : '#000000';
      content.style.backgroundColor = this.styles.fillColor
        ? this.styles.fillColor
        : COLORS['base-800'];
      content.style.color = this.styles.overrideColor ? this.color : colorBasedOnFillColor;
      content.style.width = '32px';
      content.style.height = '32px';
    }
    if (this.styles.strokeOpacity > 0) {
      content.style.borderRadius = '50%';
      content.style.border =
        this.styles.strokeOpacity > 0 ? `${borderWeight}px solid ${borderColor}` : 'none';
    }
    if (this.styles.scale && !this._options.isAxonLayer) {
      content.style.transform = `scale(${this.styles.scale})`;
    }

    const status = this.device?.properties?.cameras?.status?.toLowerCase();
    if (status === 'recording') {
      this.content.classList.add('fs__axon-recording-status');
    } else {
      this.content.classList.remove('fs__axon-recording-status');
    }
  }

  public setIcon(): this {
    const fontIcon = typeof this.icon === 'string' ? this.icon : (this.icon as any).fontIcon;
    const paths = this.getPaths(fontIcon);
    const icon = document.createElement('span');
    icon.classList.value = `fs ${fontIcon}`;
    icon.style.fontSize = this.size;
    paths.forEach((el, i) => {
      const t = document.createElement('span');
      t.classList.value = `path${i + 1}`;
      icon?.appendChild(t);
    });
    icon.classList.add('fs__advanced-feature-icon');
    this.content.appendChild(icon);
    return this;
  }

  /**
   * Used to set the icon label of the marker.
   */
  public setIconLabel(): void {
    if (this.iconLabel?.text) {
      const iconLabel = document.createElement('span');
      iconLabel.innerText = this.iconLabel.text;
      this.content.appendChild(iconLabel);
      iconLabel.classList.add('fs__advanced-feature-marker-label');
      iconLabel.style.top = `${this.iconLabel.top}px`;
      iconLabel.style.left = `${this.iconLabel.left}px`;
    }
  }

  /**
   * Used to set the features of the marker.
   * @param features are the CSS proeprties we want to set.
   */
  public setFeatures(features: any): void {
    this.feature = features;
    if (this.iconLabel?.text) {
      const existingLabel: HTMLElement = this.content.querySelector(
        '.fs__advanced-feature-marker-label',
      );
      if (existingLabel) {
        existingLabel.innerText = this._options.iconLabel.text;
        existingLabel.style.top = `${this._options.iconLabel.top}px`;
        existingLabel.style.left = `${this._options.iconLabel.left}px`;
      } else {
        const iconLabel = document.createElement('span');
        iconLabel.classList.add('fs__advanced-feature-marker-label');
        iconLabel.innerText = this._options.iconLabel.text;
        this.content.appendChild(iconLabel);
        iconLabel.style.top = `${this._options.iconLabel.top}px`;
        iconLabel.style.left = `${this._options.iconLabel.left}px`;
      }
    }
  }

  /**
   * Used to move the marker to the destination.
   */
  public move(): void {
    if (this._actualMoves >= this._expectedMoves) {
      const lonLat = new SnLonLat(this.destination.lng(), this.destination.lat());
      this.setLonLat(lonLat);
      if (this._followMode) {
        this.map.panTo(lonLat);
      }
      return;
    }
    this._actualMoves++;
    const current = this.getLonLat();
    const destination = this.destination;
    const meters2Miles = 0.000621371192;
    this._distanceToDestination =
      Math.round(
        google.maps.geometry.spherical.computeDistanceBetween(
          { lat: current.lat, lng: current.lon },
          this.destination,
        ) *
          meters2Miles *
          10,
      ) / 10;
    if (!this.map?.contains(current)) {
      this._actualMoves = this._expectedMoves;
      const lonLat = new SnLonLat(destination.lng(), destination.lat());
      this.setLonLat(lonLat);
      if (this._followMode) {
        this.map.panTo(lonLat);
      }
    }
    let newLat = current.lat + this._deltaLat;
    if (current.lat === destination.lat()) {
      newLat = destination.lat();
    }
    let newLng = current.lon + this._deltaLng;
    if (current.lon === destination.lng()) {
      newLng = destination.lng();
    }
    const newLonLat = new SnLonLat(newLng, newLat);
    this.setLonLat(newLonLat);
    if (this._followMode) {
      this.map.panTo(newLonLat);
    }
    if (this._distanceToDestination > 0.1) {
      this.moveToDestination();
    }
  }

  /**
   * Used to move the marker to the destination.
   */
  public moveToDestination(): void {
    if (!this.destination) {
      return;
    }
    this.setLonLat(new SnLonLat(this.destination.lng(), this.destination.lat()));
  }

  /**
   * Used to update the updated time of the marker.
   */
  public wasUpdated(): void {
    this.updated = Date.now();
  }

  public createNotificationBanner(data: AxonUsherAlertModel): void {
    if (!this.banner) {
      this.banner = document.createElement('div');
    }

    // Check if this notification already exists in bannerContent to prevent duplicates
    const existingNotification = this.bannerContent.find(
      (content) => content.data.notification.id === data.notification.id,
    );
    if (existingNotification) {
      return;
    }

    const icon = this._axonNotificationIconService.generateIconHTML(data?.notification?.category);
    this.banner.onmouseenter = () => {
      FsPresenceMarker.onBannerHover(this.banner, this.bannerContent);
    };
    this.bannerContent.push({
      data,
      icon: icon,
    });
    this.deviceHistory.push({
      attributes: {
        category: data.notification.category,
        device: {
          deviceId: data.device.deviceId,
          deviceType: data.device.deviceType,
        },
        timestamp: data.notification.timeOccurred,
      },
      id: data.notification.id,
      type: data.notification.type,
    });
    const uniqueIcons = [...new Set(this.bannerContent.map((content) => content.icon))];
    this.banner.innerHTML = uniqueIcons.join(' ');
    this.banner.classList.add('fs__advanced-marker-icon-banner');

    this._updateBannerCriticalState();

    this.getMarker().content.appendChild(this.banner);
    const gmMarker:
      | (google.maps.marker.AdvancedMarkerElement & { axonNotifications?: AxonUsherAlertModel[] })
      | null = this.getMarker();
    gmMarker.axonNotifications = this.bannerContent.map((content) => content.data);
  }

  public removeNotification(): void {
    if (!this.banner) {
      return;
    }
    const expandedContent = this.getMarker().element.querySelector(
      'fs__advanced-marker-icon-banner-expanded',
    );
    if (expandedContent) {
      expandedContent.remove();
    }
    this.bannerContent = [];
    this.banner.remove();
    this.banner = null;
    const gmMarker:
      | (google.maps.marker.AdvancedMarkerElement & { axonNotifications?: AxonUsherAlertModel[] })
      | null = this.getMarker();
    gmMarker.axonNotifications = null;
  }

  public addDeviceHistory(data: HistoricalInterface): void {
    this.deviceHistory.push(data);
  }

  private shouldUseSvgIcon(): boolean {
    if (!this.device?.properties) {
      return false;
    }
    
    const isOutpost = 
      this.device.properties.entityType === 'fixedalpr' && 
      (this.device.properties.model === PresenceDeviceModels.Outpost || 
       this.device.properties.model === PresenceDeviceModels.AxonOutpost);
    const featureFlagEnabled = this._currentOrganization?.settings?.enableAdvancedDotsOnMapIcon ?? false;
    return isOutpost && featureFlagEnabled;
  }

  private setSvgIcon(): void {
    if (this._svgIconInitialized) {
      this.updateSvgIconColor();
      return;
    }

    const glyph = DEVICE_GLYPHS[this.device.properties.model] || '';
    const statusColor = this.getStatusColor();

    const svgContent = OUTPOST_LIGHTPOST_SVG_TEMPLATE.replace('<!-- GLYPH_CONTENT -->', glyph);

    const container = document.createElement('div');
    container.innerHTML = svgContent;
    container.style.setProperty('--status-color', statusColor);
    container.classList.add('fs__advanced-feature-icon');

    this.content.appendChild(container);
    this._svgIconInitialized = true;
  }

  private updateSvgIconColor(): void {
    const statusColor = this.getStatusColor();
    
    if (this.content) {
      this.content.style.setProperty('--status-color', statusColor);
    }
  }
}
